import { useId, useState, type FocusEvent, type InputHTMLAttributes, type KeyboardEvent, type ReactNode, type Ref } from "react";
import "./text-field.css";
import "./combobox.css";

export type ComboboxOption = {
  /** Unico nell'elenco (diventa parte dell'`id` della voce). */
  id: string;
  label: string;
  /** Una riga piccola sotto il nome (provincia e regione di un comune). */
  detail?: string;
};

type Props = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "id" | "value" | "onChange" | "onKeyDown" | "onBlur" | "onFocus" | "role" | "type" | "aria-invalid" | "aria-describedby" | "aria-expanded" | "aria-controls" | "aria-activedescendant" | "aria-autocomplete"
> & {
  id: string;
  label: string;
  hideLabel?: boolean;
  /** Il testo scritto nella casella. */
  value: string;
  /** Si scrive: chi lo usa aggiorna il testo e cerca le voci. */
  onTextChange: (text: string) => void;
  /** Le voci da proporre adesso (già cercate da chi lo usa); vuoto: l'elenco non si apre. */
  options: readonly ComboboxOption[];
  /** Si sceglie una voce (Invio sulla voce attiva o un clic). */
  onPick: (option: ComboboxOption) => void;
  /** Il nome dell'elenco per gli screen reader («Comuni trovati»). */
  listLabel: string;
  /** L'esito della ricerca da annunciare mentre l'elenco è aperto («Cerco…», «Nessun comune per …»); `null` se niente. */
  status?: string | null;
  /** La riga sotto la casella, sempre scritta e legata alla casella (provincia e regione del comune scelto). */
  hint?: ReactNode;
  error?: string;
  /** Un'altra descrizione, scritta altrove. */
  describedBy?: string;
  /** I tasti che l'elenco non usa (Invio senza voci, Esc a elenco chiuso), per chi contiene la casella. */
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  className?: string;
  /** Arriva all'`<input>` (in React 19 `ref` è una prop). */
  ref?: Ref<HTMLInputElement>;
};

// Casella combinata ARIA (schema «combobox» con elenco): si scrive, le frecce scorrono le voci (in giro), Invio sceglie,
// Esc chiude l'elenco senza chiudere la finestra. L'elenco sta sotto la casella, nel flusso: dentro una finestra che
// scorre un elenco sovrapposto verrebbe tagliato. La ricerca la fa chi lo usa e passa le voci in `options`.
export function Combobox({
  id,
  label,
  hideLabel = false,
  value,
  onTextChange,
  options,
  onPick,
  listLabel,
  status = null,
  hint,
  error,
  describedBy,
  onKeyDown,
  onBlur,
  className,
  ref,
  ...rest
}: Props) {
  const listId = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const showList = open && options.length > 0;
  const activeIndex = Math.min(active, Math.max(options.length - 1, 0));
  const activeOption = showList ? options[activeIndex] : undefined;
  const optionId = (option: ComboboxOption) => `${listId}-${option.id}`;

  function pick(option: ComboboxOption) {
    onPick(option);
    setOpen(false);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    let handled = false;
    if (open && (options.length > 0 || status)) {
      handled = true;
      if (event.key === "Escape") setOpen(false);
      else if (event.key === "ArrowDown" && options.length > 0) setActive((activeIndex + 1) % options.length);
      else if (event.key === "ArrowUp" && options.length > 0) setActive((activeIndex - 1 + options.length) % options.length);
      else if (event.key === "Enter" && activeOption) pick(activeOption);
      else handled = false;
    }
    if (handled) {
      // Invio non manda il modulo, Esc non chiude la finestra, le frecce non spostano il cursore.
      event.preventDefault();
      return;
    }
    onKeyDown?.(event);
  }

  const description = [hint ? hintId : null, describedBy ?? null, error ? errorId : null].filter(Boolean).join(" ");
  const shownStatus = open ? status : null;
  return (
    <div className={className ? `g-field g-combobox ${className}` : "g-field g-combobox"}>
      <label className={hideLabel ? "g-field__label g-visually-hidden" : "g-field__label"} htmlFor={id}>{label}</label>
      <input
        autoComplete="off"
        {...rest}
        ref={ref}
        id={id}
        className="g-field__input"
        type="text"
        role="combobox"
        value={value}
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={activeOption ? optionId(activeOption) : undefined}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={description || undefined}
        onChange={(event) => {
          onTextChange(event.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={(event) => {
          setOpen(false);
          onBlur?.(event);
        }}
        onKeyDown={handleKeyDown}
      />
      <ul id={listId} className="g-combobox__list" role="listbox" aria-label={listLabel} hidden={!showList}>
        {options.map((option, index) => (
          <li
            key={option.id}
            id={optionId(option)}
            role="option"
            tabIndex={-1}
            aria-selected={index === activeIndex}
            className="g-combobox__option"
            // Il clic non deve togliere il focus alla casella prima di scegliere.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => pick(option)}
            onKeyDown={(event) => {
              if (event.key === "Enter") pick(option);
            }}
          >
            <span className="g-combobox__name">{option.label}</span>
            {option.detail ? <span className="g-combobox__detail">{option.detail}</span> : null}
          </li>
        ))}
      </ul>
      {/* Una sola regione che si annuncia, sempre presente (una che compare già piena spesso non si legge). */}
      <p className={shownStatus ? "g-field__hint" : "g-visually-hidden"} role="status">
        {shownStatus}
      </p>
      {hint ? (
        <p id={hintId} className="g-field__hint">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="g-field__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
